import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import { AppDetailLayout } from '@/shared/app-detail-layout';
import type { DetailSection } from '@/shared/app-detail-layout/types';
import { DeniedState, ErrorState } from '@/shared/feedback';
import { useAsyncResource } from '@/shared/hooks/use-async-resource';
import {
  CredentialStatus,
  RotateCredentialDialog,
  isForbidden,
  providerStateTone,
  toError,
} from '@/modules/ai/shared';
import { aiApi } from '@/modules/ai/services/ai-api';
import { ProviderEditForm } from './parts/provider-edit-form';
import { ProviderOverview } from './parts/provider-overview';
import type { ProviderSummary, ProviderTestResult } from '@/api/schemas/ai';

/**
 * One platform provider: what it is, how to change it, and how to rotate its
 * credential.
 *
 * The credential panel shows the STATUS and rotates the key. It never shows a
 * key, because the API has none to show.
 */
export type AiProviderDetailPageProps = {
  providerId: string;
};

export function AiProviderDetailPage({ providerId }: AiProviderDetailPageProps): ReactNode {
  const load = useCallback(() => aiApi.getPlatformProvider(providerId), [providerId]);
  const { data, error, isLoading, reload } = useAsyncResource(load);

  const [testResult, setTestResult] = useState<ProviderTestResult | null>(null);
  const [isTesting, setIsTesting] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [actionError, setActionError] = useState<Error | null>(null);

  const surface = providerSurface({ isLoading, error, data, onRetry: reload });
  if (surface !== null) return surface;

  const provider = data;
  if (provider === null) return null;

  const context: ProviderDetailContext = {
    setTestResult,
    setIsTesting,
    setIsRotating,
    setActionError,
    reload,
  };

  return (
    <AppDetailLayout
      headerVariant="accent"
      title={provider.name}
      subtitle={provider.baseUrl}
      status={{ label: provider.state, tone: providerStateTone(provider.state) }}
      breadcrumbs={[
        { label: 'AI console', href: '/platform/ai' },
        { label: provider.name },
      ]}
      tabs={[
        { id: 'overview', label: 'Overview' },
        { id: 'settings', label: 'Settings' },
        { id: 'credential', label: 'Credential' },
      ]}
      sections={buildSections({
        provider,
        testResult,
        isTesting,
        isRotating,
        actionError,
        onTest: () => {
          void runProviderTest(context, provider);
        },
        onRotate: (apiKey) => {
          void rotateProviderCredential(context, provider, apiKey);
        },
        onSaved: reload,
      })}
      tabsInHeader
    />
  );
}

type ProviderSurfaceParams = {
  isLoading: boolean;
  error: Error | null;
  data: ProviderSummary | null;
  onRetry: () => void;
};

/**
 * The surface that replaces the page body while there is nothing to show.
 *
 * `null` means "ready" — the caller carries on and renders the provider.
 */
function providerSurface(params: ProviderSurfaceParams): ReactNode | null {
  if (params.isLoading) {
    return <AppDetailLayout title="AI provider" sections={[]} isLoading />;
  }
  if (isForbidden(params.error)) {
    return (
      <AppDetailLayout
        title="AI provider"
        sections={[]}
        toolbar={
          <DeniedState title="Not permitted for your role" message="You cannot read this provider." />
        }
      />
    );
  }
  if (params.error) {
    return (
      <AppDetailLayout
        title="AI provider"
        sections={[]}
        toolbar={
          <ErrorState
            title="Could not load the provider"
            message={params.error.message}
            onRetry={params.onRetry}
          />
        }
      />
    );
  }
  if (params.data === null) {
    return (
      <AppDetailLayout
        title="AI provider"
        sections={[]}
        toolbar={
          <ErrorState title="Could not load the provider" message="No provider was returned." />
        }
      />
    );
  }
  return null;
}

type ProviderDetailContext = {
  setTestResult: (result: ProviderTestResult | null) => void;
  setIsTesting: (busy: boolean) => void;
  setIsRotating: (busy: boolean) => void;
  setActionError: (error: Error | null) => void;
  reload: () => void;
};

async function runProviderTest(
  context: ProviderDetailContext,
  provider: ProviderSummary,
): Promise<void> {
  context.setIsTesting(true);
  context.setActionError(null);
  try {
    context.setTestResult(await aiApi.testPlatformProvider(provider.id));
  } catch (cause) {
    context.setActionError(toError(cause));
  } finally {
    context.setIsTesting(false);
  }
}

async function rotateProviderCredential(
  context: ProviderDetailContext,
  provider: ProviderSummary,
  apiKey: string,
): Promise<void> {
  context.setIsRotating(true);
  context.setActionError(null);
  try {
    await aiApi.rotatePlatformCredential(provider.id, { apiKey });
    context.reload();
  } catch (cause) {
    context.setActionError(toError(cause));
  } finally {
    context.setIsRotating(false);
  }
}

type DetailSectionsParams = {
  provider: ProviderSummary;
  testResult: ProviderTestResult | null;
  isTesting: boolean;
  isRotating: boolean;
  actionError: Error | null;
  onTest: () => void;
  onRotate: (apiKey: string) => void;
  onSaved: () => void;
};

function buildSections(params: DetailSectionsParams): ReadonlyArray<DetailSection> {
  return [
    {
      id: 'overview',
      label: 'Overview',
      content: (
        <ProviderOverview
          provider={params.provider}
          testResult={params.testResult}
          isTesting={params.isTesting}
          error={params.actionError}
          onTest={params.onTest}
        />
      ),
    },
    {
      id: 'settings',
      label: 'Settings',
      content: (
        <ProviderEditForm
          key={params.provider.version}
          provider={params.provider}
          onSaved={params.onSaved}
        />
      ),
    },
    {
      id: 'credential',
      label: 'Credential',
      content: (
        <div className="space-y-4">
          {params.actionError ? (
            <ErrorState title="Action failed" message={params.actionError.message} />
          ) : null}
          <CredentialStatus provider={params.provider} />
          <RotateCredentialDialog
            providerName={params.provider.name}
            isSubmitting={params.isRotating}
            error={null}
            onSubmit={params.onRotate}
          />
        </div>
      ),
    },
  ];
}
